import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  selectCockpitMode,
  selectCockpitOffering,
  selectIsCockpitPlanningStarted,
} from '../../../../model/selectors'
import { useAttentionStatusesText } from '../../hooks/useAttentionStatusesText'
import { useIsAttentionPlanFinished } from '../../hooks/useIsAttentionPlanFinished'
import type { IAttentionStatusesUnit } from '../../types'
import {
  AttentionCompletenessOverview,
  useCompletenessOverviewRows,
} from '../attentionCompletenessOverview'
import { AttentionCalmRobot } from '../attentionRobotFigure'
import { AttentionStatusCompleteness } from '../attentionStatusCompleteness'

import styles from './AttentionStatusesEmptyState.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

interface IAttentionStatusesEmptyStateProps {
  /** The level's own size where it knows one, so the stub can still say `0 of 12 partners`. */
  totalCount?: number
  unit: IAttentionStatusesUnit
}

/**
 * What every layout of the tile draws when its level holds nothing: one stub rather than three.
 *
 * The layouts differ in how they list what is raised, and with nothing raised there is nothing to
 * lay out — a hero counting `0`, a line of text and an empty board column were three answers to
 * the same state, and the reader met a different one per layout.
 */
export const AttentionStatusesEmptyState = ({
  totalCount,
  unit,
}: IAttentionStatusesEmptyStateProps) => {
  const text = useAttentionStatusesText()
  /* The green check is a clean plan: nothing raised, measured full and signed. With nothing raised
     on a plan not finished, the tile says so in orange instead. */
  const isPlanFinished = useIsAttentionPlanFinished()
  const title = <Translation span text={isPlanFinished ? KEYS.emptyTitle : KEYS.unfinishedTitle} />
  const isPlanningStarted = useSelector(selectIsCockpitPlanningStarted)
  /* On one partner's cockpit a clean, planned scope says what it is made of instead: the
     completeness of its sections, or of its programmes on `Overall`. */
  const isPartnerCockpit = useSelector(selectCockpitMode) === 'partner'
  const completenessRows = useCompletenessOverviewRows()
  const isOverall = useSelector(selectCockpitOffering) === ProgramTypes.Overall

  /* The section stands in place of the stub, not under it: with the plan clean, what it is made
     of is the answer the tile has to give. One programme draws the `not planned` detail itself,
     before planning starts too — its sections and signature are what is left to read. */
  if (isPartnerCockpit && !isOverall && completenessRows.length > 0) {
    return (
      <div className={styles.withRobot}>
        {/* The same column the raised statuses stand in: the robot holds the plan as it is, and
            the clean bill, or what keeps it from one, is said under it. */}
        <div className={styles.robotColumn}>
          <AttentionCalmRobot state={isPlanFinished ? 'finished' : 'unfinished'} />
          <span className={styles.title}>{title}</span>
        </div>

        <div className={styles.completeness}>
          <AttentionStatusCompleteness isOverview />
        </div>
      </div>
    )
  }

  if (isPartnerCockpit && isPlanningStarted && completenessRows.length > 0) {
    return <AttentionCompletenessOverview rows={completenessRows} />
  }

  /* Nothing to read at all: the robot stands empty-handed, or holds a half done plan where
     planning is not finished. */
  return (
    <div className={styles.empty}>
      <AttentionCalmRobot state={isPlanFinished ? 'empty' : 'unfinished'} />
      <span className={styles.title}>{title}</span>
      {totalCount !== undefined && (
        <span className={styles.subtitle}>
          <Translation span {...text.phrase.fullCounter(unit, 0, totalCount)} />
        </span>
      )}
    </div>
  )
}

AttentionStatusesEmptyState.displayName = 'AttentionStatusesEmptyState'
